import React, { useCallback, useEffect, useState } from 'react';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  AppPropertiesNames,
  AppPropertiesNotUsed,
  TITLE_APP_PROPERTY,
} from 'constants/Mapping.constants';
import { isUndefined, keyBy, uniq } from 'lodash';
import {
  isLoadingObjectType,
  updateForFieldInObjectType,
  updateForObjectType,
} from 'slices/appService.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TAppName } from 'types/app.type';
import type { TObjectType } from 'types/state.interface';
import CONSTANTS from '@timeedit/types/lib/constants';
import { EAppProperties } from '@timeedit/types/lib/enums';
import { createFieldMapping } from '@timeedit/types/lib/utils/mapping.utils';
import {
  Input,
  Radio,
  type RadioChangeEvent,
  Select,
  Switch,
  Table,
  Typography,
} from '@timeedit/ui-components/antd';
import DraggableBodyRow from '../DraggableRow/DraggableRow';
import './FreeMappingFieldMapping.scss';

/* State handling here is quite problematic. So, we need to isolate this input for the allocationFieldFromObject. No further logic in here. */
const InputWrapperForAllocationFieldFromObject = ({
  inputValue,
  allocationFieldFromObjectValueChanged,
}: {
  inputValue: string | undefined;
  allocationFieldFromObjectValueChanged: (newValue: string | undefined) => void;
}) => {
  const [currentInternalValue, setCurrentInternalValue] = React.useState<
    string | undefined
  >(inputValue);
  return (
    <Input
      value={currentInternalValue}
      onChange={(event) => {
        setCurrentInternalValue(event.target.value);
      }}
      onBlur={() => allocationFieldFromObjectValueChanged(currentInternalValue)}
    ></Input>
  );
};

type TFreeMappingFieldMapping = {
  appName: TAppName;
  objectType: TObjectType;
  objectTypeIdx: number;
  rawObjectFields?: any;
  rawObjectFieldsLoading?: boolean;
};

type TData = TObjectType['fields'][number];

type Entry = [string, { label: string }] | null | undefined;

const FreeMappingFieldMapping = ({
  appName,
  objectType,
  objectTypeIdx,
  rawObjectFields,
  rawObjectFieldsLoading,
}: TFreeMappingFieldMapping) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(isLoadingObjectType(appName));

  const [dataSource, setDataSource] = useState(objectType.fields);

  // Maintaining state instead of just using the prop directly to prevent jumps in the UI when dropping rows
  useEffect(() => {
    setDataSource(objectType.fields);
  }, [objectType.fields]);

  useEffect(() => {
    if (rawObjectFields && rawObjectFields.length) {
      const indexedField = keyBy(
        objectType.fields.filter((field: any) => field.isActive),
        'fieldExtId'
      );
      const orderedKeys = uniq(
        Object.keys(indexedField).concat(
          rawObjectFields.map((item: any) => item.value)
        )
      );
      const fields = orderedKeys.map((key) => {
        const existedField = indexedField[key];
        if (existedField) {
          return createFieldMapping({
            ...existedField,
            isActive: true,
          });
        }
        return createFieldMapping({
          appFieldName: key,
          fieldExtId: key,
          fieldLabel: key,
          isActive: false,
        });
      });

      updateForObjectType({
        appName,
        objectTypeIdx,
        fields,
        hasChanges: objectType.hasChanges,
      });
    }
  }, [rawObjectFieldsLoading, objectType.objectTypeExtId]);

  const onFieldToggle = useCallback(
    (item: any, itemIndex: any) => (checked: any) => {
      const activatedFieldsCount = objectType.fields.filter(
        (field: any) => field.isActive
      ).length;
      if (checked) {
        // If first activated field -> set it as Is Label field
        dispatch(
          updateForFieldInObjectType({
            appName,
            objectTypeIdx,
            fieldIdx: itemIndex,
            isActive: true,
            appProperty:
              activatedFieldsCount === 0 ? EAppProperties.LABEL : null,
          })
        );
      } else {
        dispatch(
          updateForFieldInObjectType({
            appName,
            objectTypeIdx,
            fieldIdx: itemIndex,
            isActive: false,
            appProperty: null,
          })
        );

        if (
          item.appProperty === EAppProperties.LABEL &&
          activatedFieldsCount > 1
        ) {
          const nextLabeledFieldIndex = objectType.fields.findIndex(
            (field: any) => {
              return field.fieldExtId !== item.fieldExtId && field.isActive;
            }
          );
          dispatch(
            updateForFieldInObjectType({
              appName,
              objectTypeIdx,
              fieldIdx: nextLabeledFieldIndex,
              appProperty: EAppProperties.LABEL,
            })
          );
        }
      }
    },
    [objectType.fields]
  );

  const onSearchableToggle = useCallback(
    (item: any, itemIndex: number) => (checked: any) => {
      dispatch(
        updateForFieldInObjectType({
          appName,
          objectTypeIdx,
          fieldIdx: itemIndex,
          isSearchable: checked,
        })
      );
    },
    [objectType.fields]
  );

  const onDefaultSortingToggle = useCallback(
    (item: unknown, itemIndex: number) => (event: RadioChangeEvent) => {
      // If set to "checked" then set the field in question and remove the default sorting from all other fields
      if (event.target.checked) {
        dispatch(
          updateForFieldInObjectType({
            appName,
            objectTypeIdx,
            fieldIdx: itemIndex,
            defaultSorting: true,
          })
        );

        for (let i = 0; i < objectType.fields.length; i++) {
          if (i !== itemIndex) {
            dispatch(
              updateForFieldInObjectType({
                appName,
                objectTypeIdx,
                fieldIdx: i,
                defaultSorting: false,
              })
            );
          }
        }
      }

      // If set to "not checked" then simply change the field in question
      if (!event.target.checked) {
        dispatch(
          updateForFieldInObjectType({
            appName,
            objectTypeIdx,
            fieldIdx: itemIndex,
            defaultSorting: false,
          })
        );
      }
    },
    [objectType.fields]
  );

  const onAppPropertySelect =
    (_: any, itemIndex: number) => (appProperty: EAppProperties) => {
      dispatch(
        updateForFieldInObjectType({
          appName,
          objectTypeIdx,
          fieldIdx: itemIndex,
          appProperty,
        })
      );
    };

  const onFieldLabelChange =
    (item: any, itemIndex: number) => (newLabel: any) => {
      dispatch(
        updateForFieldInObjectType({
          appName,
          objectTypeIdx,
          fieldIdx: itemIndex,
          fieldLabel: newLabel,
        })
      );
    };

  const appPropertySortKey = (entry: Entry) => {
    if (!entry) {
      return 'wwwww';
    }
    const [property, value] = entry;
    if (property == EAppProperties.NONE) {
      return '-';
    }
    if (value) {
      return appPropertyName(entry);
    }
    return 'wwwww';
  };

  const appPropertyName = (entry: Entry) => {
    if (!entry) {
      return '-';
    }
    const [property, value] = entry;
    const name = AppPropertiesNames[property];
    if (!isUndefined(name)) {
      return name;
    }
    if (value) {
      return value.label;
    }
    return '-';
  };

  const appPropertyOptions = [
    ...Object.entries(CONSTANTS.AppPropertyProps),
    null,
  ]
    .filter((entry) => !AppPropertiesNotUsed.includes(entry?.[0]))
    .sort((a, b) => appPropertySortKey(a).localeCompare(appPropertySortKey(b)))
    .map((entry) => ({
      value: entry?.[0],
      label: appPropertyName(entry),
    }));

  const columns = [
    {
      title: 'Field',
      dataIndex: 'fieldExtId',
    },
    {
      title: 'Include',
      render: (text: any, item: any, itemIndex: number) => (
        <Switch
          checked={item.isActive}
          onChange={onFieldToggle(item, itemIndex)}
        />
      ),
    },
    {
      title: 'Searchable',
      render: (text: any, item: any, itemIndex: number) => {
        const style = !item.isActive ? { cursor: 'not-allowed' } : {};
        return (
          <Switch
            style={style}
            checked={item.isSearchable && item.isActive}
            onChange={onSearchableToggle(item, itemIndex)}
          />
        );
      },
    },
    {
      title: 'Default sorting (limited reach)',
      render: (text: any, item: any, itemIndex: number) => {
        return (
          <Radio
            disabled={!item.isActive}
            checked={item.defaultSorting}
            onChange={onDefaultSortingToggle(item, itemIndex)}
          />
        );
      },
    },
    {
      title: 'Label',
      render: (text: any, item: any, itemIndex: number) => (
        <Typography.Paragraph
          style={{ marginBottom: '0' }}
          editable={{ onChange: onFieldLabelChange(item, itemIndex) }}
        >
          {item.fieldLabel}
        </Typography.Paragraph>
      ),
    },
    {
      title: TITLE_APP_PROPERTY,
      render: (_: any, item: any, itemIndex: number) => (
        <Select
          options={appPropertyOptions}
          style={{ width: '100%', minWidth: '12rem' }}
          popupMatchSelectWidth
          optionFilterProp="label"
          showSearch
          onChange={onAppPropertySelect(item, itemIndex)}
          value={item.appProperty}
        />
      ),
    },
  ];

  if (appName === 'tePreferences') {
    columns.push({
      title: 'Allocation field from object',
      render: (_: any, item: any, itemIndex: number) => (
        <InputWrapperForAllocationFieldFromObject
          inputValue={item.allocationFieldFromObject}
          allocationFieldFromObjectValueChanged={(newValue) => {
            dispatch(
              updateForFieldInObjectType({
                appName,
                objectTypeIdx,
                fieldIdx: itemIndex,
                allocationFieldFromObject: newValue,
              })
            );
          }}
        ></InputWrapperForAllocationFieldFromObject>
      ),
    });
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prev) => {
        const activeIndex = prev.findIndex((i) => i.fieldExtId === active.id);
        const overIndex = prev.findIndex((i) => i.fieldExtId === over?.id);
        const fields = arrayMove(prev, activeIndex, overIndex);

        dispatch(
          updateForObjectType({
            appName,
            objectTypeIdx,
            fields,
          })
        );

        return fields;
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={dataSource.map((field) => field.fieldExtId)}
        strategy={verticalListSortingStrategy}
      >
        <Table<TData>
          columns={columns}
          dataSource={dataSource}
          rowKey="fieldExtId"
          pagination={false}
          loading={isLoading}
          components={{
            body: {
              row: DraggableBodyRow,
            },
          }}
        />
      </SortableContext>
    </DndContext>
  );
};

export default FreeMappingFieldMapping;
